import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Card, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";

const API_URL = process.env.REACT_APP_API_URL;

function CreatePlot() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    project_id: "",
    project_name: "",
    block_id: "",
    block_name: "",
    property_type: "",
    plot_shop_villa_no: "",
    plot_size: "",
    plot_rate: "",
    plot_address: "",
    status: "available",
    plc_percentage: "",
    resgistry_date: "",
    plot_hold: "",
    north: "",
    south: "",
    east: "",
    west: "",
    area_sqyd: "",
    road_size: "",
    unit_type: "",
    facing: "",
    smallunit: "",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    area_sqmt: "",
    unit_price: "",
    base_price: "",
    final_price: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [selectedProjectCommissionData, setSelectedProjectCommissionData] =
    useState(null);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({
    title: "",
    text: "",
    type: "",
    confirmAction: null,
  });

  const showCustomMessageModal = (title, text, type, confirmAction = null) => {
    setMessageModalContent({ title, text, type, confirmAction });
    setShowMessageModal(true);
  };

  const closeCustomMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({ title: "", text: "", type: "", confirmAction: null });
  };

  const getAuthToken = () => {
    return localStorage.getItem("token");
  };

  useEffect(() => {
    const fetchProjectsList = async () => {
      setLoading(true);
      try {
        const token = getAuthToken();
        if (!token) {
          showCustomMessageModal(
            "Authentication Error",
            "Authentication token not found. Please log in.",
            "error"
          );
          return;
        }

        const response = await fetch(`${API_URL}/project-list-block`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          showCustomMessageModal(
            "Error",
            errorData.message || "Failed to fetch projects.",
            "error"
          );
          return;
        }

        const data = await response.json();
        setProjects(data.data || []);
      } catch (err) {
        console.error("Fetch projects error:", err);
        showCustomMessageModal(
          "Error",
          err.message || "An unexpected error occurred while fetching projects.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProjectsList();
  }, []);

  useEffect(() => {
    const fetchBlocksList = async () => {
      if (formData.project_id) {
        setLoading(true);
        try {
          const token = getAuthToken();
          if (!token) {
            showCustomMessageModal(
              "Authentication Error",
              "Authentication token not found. Please log in.",
              "error"
            );
            return;
          }

          const response = await fetch(`${API_URL}/block-list-plot`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ project_id: formData.project_id }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            showCustomMessageModal(
              "Error",
              errorData.message || "Failed to fetch blocks.",
              "error"
            );
            setBlocks([]);
            return;
          }

          const data = await response.json();
          setBlocks(data.data || []);
        } catch (err) {
          console.error("Fetch blocks error:", err);
          showCustomMessageModal(
            "Error",
            err.message || "An unexpected error occurred while fetching blocks.",
            "error"
          );
          setBlocks([]);
        } finally {
          setLoading(false);
        }
      } else {
        setBlocks([]);
        setFormData((prev) => ({ ...prev, block_id: "", block_name: "" }));
      }
    };

    fetchBlocksList();
  }, [formData.project_id]);


  useEffect(() => {
    const { north, south, east, west, plot_rate, plc_percentage } = formData;

    if (north && south && east && west) {
      const avgLength = (parseFloat(north) + parseFloat(south)) / 2;
      const avgWidth = (parseFloat(east) + parseFloat(west)) / 2;

      const areaSqFt = avgLength * avgWidth;
      const areaSqYd = (areaSqFt / 9);
      const areaSqMt = (areaSqFt / 10.7639);

      setFormData(prev => ({
        ...prev,
        area_sqyd: areaSqYd.toFixed(2)
      }));

      setCalculatedValues(prev => ({
        ...prev,
        area_sqmt: areaSqMt.toFixed(2)
      }));


      if (plot_rate) {
        const rate = parseFloat(plot_rate);
        let basePrice = areaSqYd * rate;
        let finalPrice = basePrice;

        if (plc_percentage && !isNaN(parseFloat(plc_percentage))) {
          const percentage = parseFloat(plc_percentage);
          finalPrice = basePrice + (basePrice * percentage / 100);
        }

        setCalculatedValues(prev => ({
          ...prev,
          unit_price: finalPrice.toFixed(2),
          base_price: basePrice.toFixed(2),
          final_price: finalPrice.toFixed(2)
        }));
      }
    }
  }, [formData.north, formData.south, formData.east, formData.west, formData.plot_rate, formData.plc_percentage]);


  useEffect(() => {
    const { area_sqyd, plot_rate, plc_percentage, north, south, east, west } = formData;


    if (area_sqyd && !(north && south && east && west)) {
      const areaSqYd = parseFloat(area_sqyd);
      const areaSqMt = (areaSqYd * 9 / 10.7639);

      setCalculatedValues(prev => ({
        ...prev,
        area_sqmt: areaSqMt.toFixed(2)
      }));


      if (plot_rate) {
        const rate = parseFloat(plot_rate);
        let basePrice = areaSqYd * rate;
        let finalPrice = basePrice;

        if (plc_percentage && !isNaN(parseFloat(plc_percentage))) {
          const percentage = parseFloat(plc_percentage);
          finalPrice = basePrice + (basePrice * percentage / 100);
        }

        setCalculatedValues(prev => ({
          ...prev,
          unit_price: finalPrice.toFixed(2),
          base_price: basePrice.toFixed(2),
          final_price: finalPrice.toFixed(2)
        }));
      }
    }
  }, [formData.area_sqyd, formData.plot_rate, formData.plc_percentage]);


  useEffect(() => {
    const { north, south, east, west } = formData;
    if (!(north && south && east && west)) {

      setCalculatedValues(prev => ({
        ...prev,
        area_sqmt: ""
      }));
    }
  }, [formData.north, formData.south, formData.east, formData.west]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      const newData = { ...prevData, [name]: value };

      if (name === "project_id") {
        const selectedProjectObj = projects.find(
          (proj) => proj.id.toString() === value
        );
        newData.project_name = selectedProjectObj ? selectedProjectObj.name : "";
        newData.block_id = "";
        newData.block_name = "";
        setSelectedProjectCommissionData(selectedProjectObj || null);
      } else if (name === "block_id") {
        const selectedBlockObj = blocks.find(
          (block) => block.id.toString() === value
        );
        newData.block_name = selectedBlockObj ? selectedBlockObj.name : "";
      }

      return newData;
    });

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    const directRequiredFields = [
      "project_id",
      "property_type",
      "plot_shop_villa_no",
      "plot_rate",
      "plot_address",
      "road_size",
      "facing",
      "status",
      "smallunit"
    ];

    directRequiredFields.forEach((field) => {
      if (
        !formData[field] ||
        (typeof formData[field] === "string" && !formData[field].trim())
      ) {
        newErrors[field] = `${field
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase())} is required.`;
        isValid = false;
      }
    });

    if (formData.plot_rate !== "" && (isNaN(formData.plot_rate) || parseFloat(formData.plot_rate) <= 0)) {
      newErrors.plot_rate = "Rate must be a positive number.";
      isValid = false;
    }

    if (
      formData.project_id &&
      (!formData.project_name || !formData.project_name.trim())
    ) {
      newErrors.project_id =
        newErrors.project_id || "Selected project name could not be determined.";
      isValid = false;
    }

    if (
      !(formData.north && formData.south && formData.east && formData.west) &&
      !formData.area_sqyd
    ) {
      newErrors.area_sqyd = "Enter directions OR manual area";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showCustomMessageModal(
        "Validation Error",
        "Please correct the errors in the form.",
        "error"
      );
      return;
    }

    setLoading(true);

    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error"
        );
        return;
      }

      const payload = {
        project_id: formData.project_id,
        project_name: formData.project_name,
        block_id: formData.block_id,
        block_name: formData.block_name,
        document_type: formData.property_type,
        plot_shop_villa_no: formData.plot_shop_villa_no,
        north: formData.north,
        south: formData.south,
        east: formData.east,
        west: formData.west,
        area_sqyd: formData.area_sqyd,
        rate: parseFloat(formData.plot_rate),
        address: formData.plot_address,
        status: formData.status,
        road_size: formData.road_size,
        unit_type: formData.unit_type,
        facing: formData.facing,
        plc_percentage: formData.plc_percentage,
        smallunit: formData.smallunit,
      };

      if (formData.property_type === "registry" || formData.property_type === "registrywithpatta") {
        payload.resgistry_date = formData.resgistry_date;
      }

      const response = await fetch(`${API_URL}/plot-create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          errorData.message || "Failed to create plot.",
          "error"
        );
        return;
      }

      const result = await response.json();


      const successMessage = `Plot created successfully!\n\n` +
        `Area: ${result.data.area_sqyd} SqYd (${result.data.unit_area_sqmt} SqMt)\n` +
        `Base Price: ${result.data.calculation?.base_price || 'N/A'}\n` +
        `PLC: ${result.data.calculation?.plc_percentage || 0}%\n` +
        `Final Price: ${result.data.unit_price}`;

      showCustomMessageModal(
        "Success",
        successMessage,
        "success"
      );


      setFormData({
        project_id: "",
        project_name: "",
        block_id: "",
        block_name: "",
        property_type: "",
        plot_shop_villa_no: "",
        north: "",
        south: "",
        east: "",
        west: "",
        area_sqyd: "",
        plot_rate: "",
        resgistry_date: "",
        plot_hold: "",
        plot_address: "",
        plc_percentage: "",
        road_size: "",
        unit_type: "",
        facing: "",
        status: "available",
        smallunit: "",
      });

      setCalculatedValues({
        area_sqmt: "",
        unit_price: "",
        base_price: "",
        final_price: "",
      });

      setErrors({});
      setBlocks([]);
      setSelectedProjectCommissionData(null);

      setTimeout(() => {
        navigate("/all-plot");
      }, 3000);
    } catch (err) {
      console.error("Error creating plot:", err);
      showCustomMessageModal(
        "Error",
        err.message || "An unexpected error occurred.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getCurrentCommission = () => {
    if (!selectedProjectCommissionData || !formData.property_type) return "N/A";

    switch (formData.property_type) {
      case "Resident":
        return `${selectedProjectCommissionData.resident_commission || 0}`;
      case "Commercial":
        return `${selectedProjectCommissionData.commercial_commission || 0}`;
      case "Corner Resident":
        return `${selectedProjectCommissionData.corner_resident_commission || 0
          }`;
      case "Corner Commercial":
        return `${selectedProjectCommissionData.corner_commercial_commission || 0
          }`;
      default:
        return "N/A";
    }
  };

  // return (
  //   <div className="mt-2">
  //     <div className="row">
  //       <div className="col-md-12">
  //         <div className="card">
  //           <div className="card-header">
  //             <div className="d-flex align-items-center justify-content-between flex-wrap-mobile gap-2">
  //               <div className="titlepage">
  //                 <h3>Create New Plot/Shop/Villa</h3>
  //               </div>
  //               <button
  //                 className="btn btn-primary d-flex gap-1"
  //                 onClick={() => navigate(-1)}
  //               >
  //                 <IoMdArrowRoundBack />
  //                 Back
  //               </button>
  //             </div>
  //           </div>
  //           <div className="card-body">
  //             <Form onSubmit={handleSubmit}>
  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formProjectId">
  //                     <Form.Label>
  //                       Select Project <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       as="select"
  //                       name="project_id"
  //                       value={formData.project_id}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.project_id}
  //                       disabled={loading || projects.length === 0}
  //                     >
  //                       <option value="">Select a Project</option>
  //                       {projects.map((project) => (
  //                         <option key={project.id} value={project.id}>
  //                           {project.name}
  //                         </option>
  //                       ))}
  //                     </Form.Control>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.project_id}
  //                     </Form.Control.Feedback>
  //                     {loading && projects.length === 0 && (
  //                       <small className="text-muted">Loading projects...</small>
  //                     )}
  //                     {!loading && projects.length === 0 && (
  //                       <small className="text-danger">
  //                         No projects available. Please create one first.
  //                       </small>
  //                     )}
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formBlockId">
  //                     <Form.Label>
  //                       Select Block
  //                     </Form.Label>
  //                     <Form.Control
  //                       as="select"
  //                       name="block_id"
  //                       value={formData.block_id}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.block_id}
  //                       disabled={!formData.project_id || loading || blocks.length === 0}
  //                     >
  //                       <option value="">Select a Block</option>
  //                       {blocks.map((block) => (
  //                         <option key={block.id} value={block.id}>
  //                           {block.name}
  //                         </option>
  //                       ))}
  //                     </Form.Control>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.block_id}
  //                     </Form.Control.Feedback>
  //                     {formData.project_id && loading && blocks.length === 0 && (
  //                       <small className="text-muted">Loading blocks...</small>
  //                     )}
  //                     {formData.project_id && !loading && blocks.length === 0 && (
  //                       <small className="text-danger">
  //                         No blocks available for this project. Please create one
  //                         first.
  //                       </small>
  //                     )}
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPlotShopVillaNo">
  //                     <Form.Label>
  //                       Plot/Shop/Villa No. <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       type="text"
  //                       name="plot_shop_villa_no"
  //                       placeholder="e.g., A-101, Shop-5, Villa-B2"
  //                       value={formData.plot_shop_villa_no}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.plot_shop_villa_no}
  //                     />
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.plot_shop_villa_no}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formStatus">
  //                     <Form.Label>
  //                       Status <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       as="select"
  //                       name="status"
  //                       value={formData.status}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.status}
  //                     >
  //                       <option value="available">Available</option>
  //                       <option value="sold">Sold</option>
  //                     </Form.Control>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.status}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={3}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>East</Form.Label>
  //                     <Form.Control
  //                       name="east"
  //                       placeholder="Enter East (e.g. 100 ft)"
  //                       value={formData.east}
  //                       onChange={handleChange}
  //                     />
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={3}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>West</Form.Label>
  //                     <Form.Control
  //                       name="west"
  //                       placeholder="Enter West (e.g. 100 ft)"
  //                       value={formData.west}
  //                       onChange={handleChange}
  //                     />
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={3}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>North</Form.Label>
  //                     <Form.Control
  //                       name="north"
  //                       placeholder="Enter North (e.g. 100 ft)"
  //                       value={formData.north}
  //                       onChange={handleChange}
  //                     />
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={3}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>South</Form.Label>
  //                     <Form.Control
  //                       name="south"
  //                       placeholder="Enter South (e.g. 100 ft)"
  //                       value={formData.south}
  //                       onChange={handleChange}
  //                     />
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>
  //                       Area (SqYd)
  //                       {!(formData.north && formData.south && formData.east && formData.west) && (
  //                         <span className="text-danger">*</span>
  //                       )}
  //                     </Form.Label>
  //                     <Form.Control
  //                       name="area_sqyd"
  //                       placeholder="Area (SqYd) - Auto-calculated or manual"
  //                       value={formData.area_sqyd}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.area_sqyd}
  //                       disabled={!!(formData.north && formData.south && formData.east && formData.west)}
  //                     />
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.area_sqyd}
  //                     </Form.Control.Feedback>
  //                     {!(formData.north && formData.south && formData.east && formData.west) && (
  //                       <Form.Text className="text-muted">
  //                         Enter directions above OR enter area manually here
  //                       </Form.Text>
  //                     )}
  //                     {(formData.north && formData.south && formData.east && formData.west) && (
  //                       <Form.Text className="text-success">
  //                         Auto-calculated from directions
  //                       </Form.Text>
  //                     )}
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={6}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>Area (SqMt)</Form.Label>
  //                     <Form.Control
  //                       type="text"
  //                       name="unit_area_sqmt"
  //                       value={calculatedValues.area_sqmt}
  //                       disabled
  //                       style={{ backgroundColor: '#f8f9fa' }}
  //                       placeholder="Auto-calculated"
  //                     />
  //                     <Form.Text className="text-muted">
  //                       Auto-calculated from area in SqYd
  //                     </Form.Text>
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPlotSize">
  //                     <Form.Label>
  //                       PLC (%)
  //                       <span className="text-muted ms-2">(e.g., 2%)</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       type="text"
  //                       name="plc_percentage"
  //                       placeholder="e.g., 2"
  //                       value={formData.plc_percentage}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.plc_percentage}
  //                     />
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.plc_percentage}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPlotRate">
  //                     <Form.Label>
  //                       Rate <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       type="number"
  //                       name="plot_rate"
  //                       placeholder="e.g., 5000"
  //                       value={formData.plot_rate}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.plot_rate}
  //                     />
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.plot_rate}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>Base Price</Form.Label>
  //                     <Form.Control
  //                       type="text"
  //                       name="base_price"
  //                       value={calculatedValues.base_price}
  //                       disabled
  //                       style={{ backgroundColor: '#f8f9fa' }}
  //                       placeholder="Auto-calculated"
  //                     />
  //                     <Form.Text className="text-muted">
  //                       Area × Rate
  //                     </Form.Text>
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={6}>
  //                   <Form.Group className="mb-3">
  //                     <Form.Label>Final Unit Price (with PLC)</Form.Label>
  //                     <Form.Control
  //                       type="text"
  //                       name="unit_price"
  //                       value={calculatedValues.unit_price}
  //                       disabled
  //                       style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}
  //                       placeholder="Auto-calculated"
  //                     />
  //                     <Form.Text className="text-muted">
  //                       Base price + PLC percentage
  //                     </Form.Text>
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPlotSqYd">
  //                     <Form.Label>
  //                       Road Size <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       type="text"
  //                       name="road_size"
  //                       placeholder="e.g., 30 feet"
  //                       value={formData.road_size}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.road_size}
  //                     />
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.road_size}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPlotSqYd">
  //                     <Form.Label>
  //                       Unit Type
  //                     </Form.Label>
  //                     <Form.Control
  //                       type="text"
  //                       name="unit_type"
  //                       placeholder="e.g., corner"
  //                       value={formData.unit_type}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.unit_type}
  //                     />
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.unit_type}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPlotSqYd">
  //                     <Form.Label>
  //                       Small Unit <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Select
  //                       name="smallunit"
  //                       value={formData.smallunit}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.smallunit}
  //                     >
  //                       <option value="">Select Option</option>
  //                       <option value="yes">Yes</option>
  //                       <option value="no">No</option>
  //                     </Form.Select>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.smallunit}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPlotFacing">
  //                     <Form.Label>
  //                       Facing <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Select
  //                       name="facing"
  //                       value={formData.facing}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.facing}
  //                     >
  //                       <option value="">Select Facing</option>
  //                       <option value="east">East</option>
  //                       <option value="west">West</option>
  //                       <option value="north">North</option>
  //                       <option value="south">South</option>
  //                     </Form.Select>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.facing}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formPropertyType">
  //                     <Form.Label>
  //                       Document Type <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       as="select"
  //                       name="property_type"
  //                       value={formData.property_type}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.property_type}
  //                     >
  //                       <option value="">Select Option</option>
  //                       <option value="registry">Registry</option>
  //                       <option value="patta">Patta</option>
  //                       <option value="registrywithpatta">
  //                         Registry with Patta
  //                       </option>
  //                     </Form.Control>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.property_type}
  //                     </Form.Control.Feedback>
  //                   </Form.Group>
  //                 </Col>
  //               </Row>

  //               <Form.Group className="mb-4" controlId="formPlotAddress">
  //                 <Form.Label>
  //                   Address <span className="text-danger">*</span>
  //                 </Form.Label>
  //                 <Form.Control
  //                   as="textarea"
  //                   rows={3}
  //                   name="plot_address"
  //                   placeholder="Enter full plot address"
  //                   value={formData.plot_address}
  //                   onChange={handleChange}
  //                   isInvalid={!!errors.plot_address}
  //                 />
  //                 <Form.Control.Feedback type="invalid">
  //                   {errors.plot_address}
  //                 </Form.Control.Feedback>
  //               </Form.Group>

  //               <div className="submitbutton">
  //                 <Button
  //                   type="submit"
  //                   className="submitbutton_design"
  //                   disabled={loading}
  //                 >
  //                   {loading ? "Creating..." : "Create Plot"}
  //                 </Button>
  //               </div>
  //             </Form>
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     {/* Custom Message Modal */}
  //     {showMessageModal && (
  //       <div
  //         className="modal d-block"
  //         tabIndex="-1"
  //         style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
  //       >
  //         <div className="modal-dialog modal-dialog-centered">
  //           <div
  //             className={`modal-content ${messageModalContent.type === "success"
  //               ? "border-success"
  //               : messageModalContent.type === "error"
  //                 ? "border-danger"
  //                 : "border-warning"
  //               }`}
  //           >
  //             <div className="modal-header d-flex justify-content-between align-items-center">
  //               <h5
  //                 className={`modal-title ${messageModalContent.type === "success"
  //                   ? "text-success"
  //                   : messageModalContent.type === "error"
  //                     ? "text-danger"
  //                     : "text-warning"
  //                   }`}
  //               >
  //                 {messageModalContent.title}
  //               </h5>
  //               <button
  //                 type="button"
  //                 className="btn-close"
  //                 aria-label="Close"
  //                 onClick={closeCustomMessageModal}
  //               ></button>
  //             </div>
  //             <div className="modal-body text-secondary">
  //               <p style={{ whiteSpace: 'pre-line' }}>{messageModalContent.text}</p>
  //             </div>
  //             <div className="modal-footer justify-content-center">
  //               {messageModalContent.confirmAction ? (
  //                 <>
  //                   <Button variant="secondary" onClick={closeCustomMessageModal}>
  //                     Cancel
  //                   </Button>
  //                   <Button
  //                     variant={
  //                       messageModalContent.type === "warning" ? "warning" : "primary"
  //                     }
  //                     onClick={() => {
  //                       messageModalContent.confirmAction();
  //                       closeCustomMessageModal();
  //                     }}
  //                   >
  //                     Confirm
  //                   </Button>
  //                 </>
  //               ) : (
  //                 <Button
  //                   variant={
  //                     messageModalContent.type === "success"
  //                       ? "success"
  //                       : messageModalContent.type === "error"
  //                         ? "danger"
  //                         : "primary"
  //                   }
  //                   onClick={closeCustomMessageModal}
  //                 >
  //                   OK
  //                 </Button>
  //               )}
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     )}
  //   </div>
  // );
return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              🏢
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Create New Plot/Shop/Villa
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Add a new unit to your project
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              style={{
                padding: "12px 24px",
                borderRadius: "12px",
                border: "none",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: "700",
                letterSpacing: "0.5px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 8px 24px rgba(30, 87, 153, 0.3)",
              }}
            >
              <IoMdArrowRoundBack /> Back
            </button>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "30px" }}>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formProjectId">
                      <Form.Label>
                        Select Project <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        name="project_id"
                        value={formData.project_id}
                        onChange={handleChange}
                        isInvalid={!!errors.project_id}
                        disabled={loading || projects.length === 0}
                      >
                        <option value="">Select a Project</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.project_id}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formBlockId">
                      <Form.Label>Select Block</Form.Label>
                      <Form.Control
                        as="select"
                        name="block_id"
                        value={formData.block_id}
                        onChange={handleChange}
                        isInvalid={!!errors.block_id}
                        disabled={
                          !formData.project_id ||
                          loading ||
                          blocks.length === 0
                        }
                      >
                        <option value="">Select a Block</option>
                        {blocks.map((block) => (
                          <option key={block.id} value={block.id}>
                            {block.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.block_id}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPlotShopVillaNo">
                      <Form.Label>
                        Plot/Shop/Villa No.{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="plot_shop_villa_no"
                        placeholder="e.g., A-101, Shop-5, Villa-B2"
                        value={formData.plot_shop_villa_no}
                        onChange={handleChange}
                        isInvalid={!!errors.plot_shop_villa_no}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.plot_shop_villa_no}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formStatus">
                      <Form.Label>
                        Status <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        isInvalid={!!errors.status}
                      >
                        <option value="available">Available</option>
                        <option value="sold">Sold</option>
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.status}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>East</Form.Label>
                      <Form.Control
                        name="east"
                        placeholder="Enter East (e.g. 100 ft)"
                        value={formData.east}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>West</Form.Label>
                      <Form.Control
                        name="west"
                        placeholder="Enter West (e.g. 100 ft)"
                        value={formData.west}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>North</Form.Label>
                      <Form.Control
                        name="north"
                        placeholder="Enter North (e.g. 100 ft)"
                        value={formData.north}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>South</Form.Label>
                      <Form.Control
                        name="south"
                        placeholder="Enter South (e.g. 100 ft)"
                        value={formData.south}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Area (SqYd)
                        {!(
                          formData.north &&
                          formData.south &&
                          formData.east &&
                          formData.west
                        ) && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        name="area_sqyd"
                        placeholder="Area (SqYd) - Auto-calculated or manual"
                        value={formData.area_sqyd}
                        onChange={handleChange}
                        isInvalid={!!errors.area_sqyd}
                        disabled={
                          !!(
                            formData.north &&
                            formData.south &&
                            formData.east &&
                            formData.west
                          )
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.area_sqyd}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Area (SqMt)</Form.Label>
                      <Form.Control
                        type="text"
                        name="unit_area_sqmt"
                        value={calculatedValues.area_sqmt}
                        disabled
                        style={{ backgroundColor: "#f8f9fa" }}
                        placeholder="Auto-calculated"
                      />
                      <Form.Text className="text-muted">
                        Auto-calculated from area in SqYd
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPlotSize">
                      <Form.Label>
                        PLC (%)<span className="text-muted ms-2">(e.g., 2%)</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="plc_percentage"
                        placeholder="e.g., 2"
                        value={formData.plc_percentage}
                        onChange={handleChange}
                        isInvalid={!!errors.plc_percentage}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.plc_percentage}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPlotRate">
                      <Form.Label>
                        Rate <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        name="plot_rate"
                        placeholder="e.g., 5000"
                        value={formData.plot_rate}
                        onChange={handleChange}
                        isInvalid={!!errors.plot_rate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.plot_rate}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Base Price</Form.Label>
                      <Form.Control
                        type="text"
                        name="base_price"
                        value={calculatedValues.base_price}
                        disabled
                        style={{ backgroundColor: "#f8f9fa" }}
                        placeholder="Auto-calculated"
                      />
                      <Form.Text className="text-muted">Area × Rate</Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Final Unit Price (with PLC)</Form.Label>
                      <Form.Control
                        type="text"
                        name="unit_price"
                        value={calculatedValues.unit_price}
                        disabled
                        style={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                        }}
                        placeholder="Auto-calculated"
                      />
                      <Form.Text className="text-muted">
                        Base price + PLC percentage
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPlotSqYd">
                      <Form.Label>
                        Road Size <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="road_size"
                        placeholder="e.g., 30 feet"
                        value={formData.road_size}
                        onChange={handleChange}
                        isInvalid={!!errors.road_size}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.road_size}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPlotSqYd">
                      <Form.Label>Unit Type</Form.Label>
                      <Form.Control
                        type="text"
                        name="unit_type"
                        placeholder="e.g., corner"
                        value={formData.unit_type}
                        onChange={handleChange}
                        isInvalid={!!errors.unit_type}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.unit_type}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPlotSqYd">
                      <Form.Label>
                        Small Unit <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="smallunit"
                        value={formData.smallunit}
                        onChange={handleChange}
                        isInvalid={!!errors.smallunit}
                      >
                        <option value="">Select Option</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.smallunit}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPlotFacing">
                      <Form.Label>
                        Facing <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="facing"
                        value={formData.facing}
                        onChange={handleChange}
                        isInvalid={!!errors.facing}
                      >
                        <option value="">Select Facing</option>
                        <option value="east">East</option>
                        <option value="west">West</option>
                        <option value="north">North</option>
                        <option value="south">South</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.facing}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formPropertyType">
                      <Form.Label>
                        Document Type <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        name="property_type"
                        value={formData.property_type}
                        onChange={handleChange}
                        isInvalid={!!errors.property_type}
                      >
                        <option value="">Select Option</option>
                        <option value="registry">Registry</option>
                        <option value="patta">Patta</option>
                        <option value="registrywithpatta">
                          Registry with Patta
                        </option>
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.property_type}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4" controlId="formPlotAddress">
                  <Form.Label>
                    Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="plot_address"
                    placeholder="Enter full plot address"
                    value={formData.plot_address}
                    onChange={handleChange}
                    isInvalid={!!errors.plot_address}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.plot_address}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Submit Button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingTop: "20px",
                    borderTop: "1.5px dashed #e2e8f0",
                  }}
                >
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: "13px 42px",
                      borderRadius: "12px",
                      border: "none",
                      background: loading
                        ? "#cbd5e1"
                        : "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      color: "#ffffff",
                      fontSize: "14.5px",
                      fontWeight: "800",
                      letterSpacing: "0.5px",
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 24px rgba(30, 87, 153, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      minWidth: "220px",
                    }}
                  >
                    {loading ? (
                      <>
                        <span
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid #ffffff",
                            borderRadius: "50%",
                            display: "inline-block",
                            animation: "spin 0.8s linear infinite",
                          }}
                        />
                        Creating...
                      </>
                    ) : (
                      "Create Plot"
                    )}
                  </button>
                </div>

                {/* Footer Note */}
                <div
                  style={{
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "1.5px dashed #e2e8f0",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12.5px",
                      color: "#94a3b8",
                      fontWeight: "500",
                    }}
                  >
                    🔒 All fields marked with{" "}
                    <span style={{ color: "#dc2626" }}>*</span> are required
                  </p>
                </div>
              </Form>
            </div>
          </div>

          {/* Footer Copyright */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      {/* 🎯 MESSAGE MODAL */}
      {showMessageModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className={`modal-content ${
                messageModalContent.type === "success"
                  ? "border-success"
                  : messageModalContent.type === "error"
                    ? "border-danger"
                    : "border-warning"
              }`}
            >
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5
                  className={`modal-title ${
                    messageModalContent.type === "success"
                      ? "text-success"
                      : messageModalContent.type === "error"
                        ? "text-danger"
                        : "text-warning"
                  }`}
                >
                  {messageModalContent.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeCustomMessageModal}
                ></button>
              </div>
              <div className="modal-body text-secondary">
                <p style={{ whiteSpace: "pre-line" }}>
                  {messageModalContent.text}
                </p>
              </div>
              <div className="modal-footer justify-content-center">
                {messageModalContent.confirmAction ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={closeCustomMessageModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={
                        messageModalContent.type === "warning"
                          ? "warning"
                          : "primary"
                      }
                      onClick={() => {
                        messageModalContent.confirmAction();
                        closeCustomMessageModal();
                      }}
                    >
                      Confirm
                    </Button>
                  </>
                ) : (
                  <Button
                    variant={
                      messageModalContent.type === "success"
                        ? "success"
                        : messageModalContent.type === "error"
                          ? "danger"
                          : "primary"
                    }
                    onClick={closeCustomMessageModal}
                  >
                    OK
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default CreatePlot;